import { fitCubics, normalize } from "./fit";
import { segmentPoint, VERB } from "./pathcmds";
import type { Cmds } from "./shapes";

/**
 * SVG path data → path commands (absolute; H/V → L; S/T expanded; arcs → cubic Béziers).
 * Malformed input stops parsing at the first error, keeping what was parsed (as browsers do).
 */
export function parseSvgPath(d: string): Cmds {
  const out: Cmds = [];
  const tokens = d.match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+\.?)(?:[eE][-+]?\d+)?/g) ?? [];
  let i = 0;
  let cx = 0, cy = 0; // current point
  let sx = 0, sy = 0; // subpath start
  let lastCtrl: [number, number] | null = null; // for S/T reflection
  let lastCmd = "";
  const isNum = (t: string | undefined) => t !== undefined && !/^[a-zA-Z]$/.test(t);
  const n = () => {
    const t = tokens[i++];
    if (!isNum(t)) throw new Error("expected number");
    return parseFloat(t!);
  };
  const flag = () => {
    // Arc flags may be written without separators ("a1 1 0 01 2 2").
    const t = tokens[i];
    if (t === undefined) throw new Error("expected flag");
    if (t.length > 1 && (t[0] === "0" || t[0] === "1") && !t.includes(".")) {
      tokens[i] = t.slice(1);
      return t[0] === "1";
    }
    i++;
    return parseFloat(t) !== 0;
  };
  try {
    let cmd = "";
    while (i < tokens.length) {
      if (!isNum(tokens[i])) cmd = tokens[i++];
      else if (!cmd) throw new Error("path must start with a command");
      else if (cmd === "M") cmd = "L"; // implicit lineto after moveto
      else if (cmd === "m") cmd = "l";
      const rel = cmd === cmd.toLowerCase();
      const ox = rel ? cx : 0, oy = rel ? cy : 0;
      switch (cmd.toUpperCase()) {
        case "M": {
          cx = n() + ox;
          cy = n() + oy;
          sx = cx;
          sy = cy;
          out.push(VERB.MOVE, cx, cy);
          lastCtrl = null;
          break;
        }
        case "L":
          cx = n() + ox;
          cy = n() + oy;
          out.push(VERB.LINE, cx, cy);
          lastCtrl = null;
          break;
        case "H":
          cx = n() + ox;
          out.push(VERB.LINE, cx, cy);
          lastCtrl = null;
          break;
        case "V":
          cy = n() + oy;
          out.push(VERB.LINE, cx, cy);
          lastCtrl = null;
          break;
        case "C": {
          const x1 = n() + ox, y1 = n() + oy, x2 = n() + ox, y2 = n() + oy;
          cx = n() + ox;
          cy = n() + oy;
          out.push(VERB.CUBIC, x1, y1, x2, y2, cx, cy);
          lastCtrl = [x2, y2];
          break;
        }
        case "S": {
          const [rx, ry]: [number, number] = lastCtrl && /[CS]/i.test(lastCmd) ? [2 * cx - lastCtrl[0], 2 * cy - lastCtrl[1]] : [cx, cy];
          const x2 = n() + ox, y2 = n() + oy;
          cx = n() + ox;
          cy = n() + oy;
          out.push(VERB.CUBIC, rx, ry, x2, y2, cx, cy);
          lastCtrl = [x2, y2];
          break;
        }
        case "Q": {
          const x1 = n() + ox, y1 = n() + oy;
          cx = n() + ox;
          cy = n() + oy;
          out.push(VERB.QUAD, x1, y1, cx, cy);
          lastCtrl = [x1, y1];
          break;
        }
        case "T": {
          const [x1, y1]: [number, number] = lastCtrl && /[QT]/i.test(lastCmd) ? [2 * cx - lastCtrl[0], 2 * cy - lastCtrl[1]] : [cx, cy];
          cx = n() + ox;
          cy = n() + oy;
          out.push(VERB.QUAD, x1, y1, cx, cy);
          lastCtrl = [x1, y1];
          break;
        }
        case "A": {
          const rx = Math.abs(n()), ry = Math.abs(n()), rot = n(), large = flag(), sweep = flag();
          const x = n() + ox, y = n() + oy;
          out.push(...arcToCubics(cx, cy, rx, ry, rot, large, sweep, x, y));
          cx = x;
          cy = y;
          lastCtrl = null;
          break;
        }
        case "Z":
          out.push(VERB.CLOSE);
          cx = sx;
          cy = sy;
          lastCtrl = null;
          break;
        default:
          throw new Error(`unknown command ${cmd}`);
      }
      lastCmd = cmd;
    }
  } catch {
    /* keep what parsed */
  }
  return out;
}

/** Endpoint-parameterised elliptical arc → cubic segments (SVG 1.1 implementation notes F.6). */
export function arcToCubics(x1: number, y1: number, rx: number, ry: number, rotDeg: number, large: boolean, sweep: boolean, x2: number, y2: number): Cmds {
  if ((x1 === x2 && y1 === y2) || rx === 0 || ry === 0) return [VERB.LINE, x2, y2];
  const phi = (rotDeg * Math.PI) / 180;
  const cos = Math.cos(phi), sin = Math.sin(phi);
  const dx = (x1 - x2) / 2, dy = (y1 - y2) / 2;
  const x1p = cos * dx + sin * dy, y1p = -sin * dx + cos * dy;
  // Scale radii up if they can't span the endpoints.
  const lambda = (x1p * x1p) / (rx * rx) + (y1p * y1p) / (ry * ry);
  if (lambda > 1) {
    rx *= Math.sqrt(lambda);
    ry *= Math.sqrt(lambda);
  }
  const num = rx * rx * ry * ry - rx * rx * y1p * y1p - ry * ry * x1p * x1p;
  const den = rx * rx * y1p * y1p + ry * ry * x1p * x1p;
  let k = Math.sqrt(Math.max(0, num / den));
  if (large === sweep) k = -k;
  const cxp = (k * rx * y1p) / ry, cyp = (-k * ry * x1p) / rx;
  const cx = cos * cxp - sin * cyp + (x1 + x2) / 2, cy = sin * cxp + cos * cyp + (y1 + y2) / 2;
  const angle = (ux: number, uy: number, vx: number, vy: number) => {
    const a = Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
    return a;
  };
  const theta1 = angle(1, 0, (x1p - cxp) / rx, (y1p - cyp) / ry);
  let dtheta = angle((x1p - cxp) / rx, (y1p - cyp) / ry, (-x1p - cxp) / rx, (-y1p - cyp) / ry);
  if (!sweep && dtheta > 0) dtheta -= 2 * Math.PI;
  if (sweep && dtheta < 0) dtheta += 2 * Math.PI;
  const segs = Math.max(1, Math.ceil(Math.abs(dtheta) / (Math.PI / 2)));
  const delta = dtheta / segs;
  const t = (4 / 3) * Math.tan(delta / 4);
  const out: Cmds = [];
  let a = theta1;
  const pt = (ang: number): [number, number] => {
    const ex = rx * Math.cos(ang), ey = ry * Math.sin(ang);
    return [cos * ex - sin * ey + cx, sin * ex + cos * ey + cy];
  };
  const deriv = (ang: number): [number, number] => {
    const ex = -rx * Math.sin(ang), ey = ry * Math.cos(ang);
    return [cos * ex - sin * ey, sin * ex + cos * ey];
  };
  for (let s = 0; s < segs; s++) {
    const b = a + delta;
    const [p0x, p0y] = pt(a), [p3x, p3y] = pt(b);
    const [d0x, d0y] = deriv(a), [d3x, d3y] = deriv(b);
    out.push(VERB.CUBIC, p0x + t * d0x, p0y + t * d0y, p3x - t * d3x, p3y - t * d3y, p3x, p3y);
    a = b;
  }
  // Land exactly on the endpoint.
  out[out.length - 2] = x2;
  out[out.length - 1] = y2;
  return out;
}

/** Apply an affine [a,b,c,d,e,f] to every point (conic weights untouched). */
export function transformCmds(cmds: Cmds, m: readonly number[]): Cmds {
  const out: Cmds = [];
  let i = 0;
  while (i < cmds.length) {
    const v = cmds[i++];
    out.push(v);
    const pairs = v === VERB.MOVE || v === VERB.LINE ? 1 : v === VERB.QUAD || v === VERB.CONIC ? 2 : v === VERB.CUBIC ? 3 : 0;
    for (let k = 0; k < pairs; k++) {
      const x = cmds[i++], y = cmds[i++];
      out.push(m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]);
    }
    if (v === VERB.CONIC) out.push(cmds[i++]);
  }
  return out;
}

/** Bounds of all control/end points (a cheap, conservative bounding box). */
export function cmdsBounds(cmds: Cmds): { x: number; y: number; w: number; h: number } | null {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  let i = 0;
  while (i < cmds.length) {
    const v = cmds[i++];
    const pairs = v === VERB.MOVE || v === VERB.LINE ? 1 : v === VERB.QUAD || v === VERB.CONIC ? 2 : v === VERB.CUBIC ? 3 : 0;
    for (let k = 0; k < pairs; k++) {
      const x = cmds[i++], y = cmds[i++];
      x0 = Math.min(x0, x);
      y0 = Math.min(y0, y);
      x1 = Math.max(x1, x);
      y1 = Math.max(y1, y);
    }
    if (v === VERB.CONIC) i++;
  }
  return Number.isFinite(x0) ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0 } : null;
}

/**
 * Replace conic segments (Skia emits them for circles and boolean results) with cubics fitted
 * within `tolerance`, so commands can be written to SVG/PDF and edited as Béziers.
 */
export function conicsToCubics(cmds: Cmds, tolerance = 0.05): Cmds {
  const out: Cmds = [];
  let i = 0;
  let cx = 0, cy = 0, sx = 0, sy = 0;
  while (i < cmds.length) {
    const v = cmds[i++];
    if (v === VERB.CONIC) {
      const x1 = cmds[i++], y1 = cmds[i++], x2 = cmds[i++], y2 = cmds[i++], w = cmds[i++];
      const seg = { kind: "conic" as const, p0: { x: cx, y: cy }, c: { x: x1, y: y1 }, p1: { x: x2, y: y2 }, w };
      const n = 24;
      const pts = Array.from({ length: n + 1 }, (_, k) => segmentPoint(seg, k / n));
      const t1 = normalize({ x: x1 - cx, y: y1 - cy }), t2 = normalize({ x: x1 - x2, y: y1 - y2 });
      for (const b of fitCubics(pts, t1, t2, { tolerance, params: pts.map((_, k) => k / n) })) out.push(VERB.CUBIC, b[1].x, b[1].y, b[2].x, b[2].y, b[3].x, b[3].y);
      cx = x2;
      cy = y2;
      continue;
    }
    out.push(v);
    const pairs = v === VERB.MOVE || v === VERB.LINE ? 1 : v === VERB.QUAD ? 2 : v === VERB.CUBIC ? 3 : 0;
    for (let k = 0; k < pairs; k++) {
      cx = cmds[i++];
      cy = cmds[i++];
      out.push(cx, cy);
    }
    if (v === VERB.MOVE) [sx, sy] = [cx, cy];
    if (v === VERB.CLOSE) [cx, cy] = [sx, sy];
  }
  return out;
}
